// src/components/organisms/Navbar.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  IconButton,
  Grid,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Snackbar,
} from "@mui/material";
import { useTheme } from "styled-components";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../../assets/kbean-nav-logo.png";
import account from "../../assets/account-icon.png";
import { auth } from "../../firebase";
import useStore from "../../store/useStore"; // Import Zustand store

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Define snackbarOpen
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Define snackbarMessage
  const theme = useTheme();
  const navigate = useNavigate();
  const { userProfile, clearUserProfile } = useStore(); // Access Zustand store

  // Function to handle user logout and clear localStorage & Zustand
  const handleLogout = async () => {
    try {
      await auth.signOut();

      // Clear Zustand user profile
      clearUserProfile();

      // Show logout success message
      setSnackbarMessage("Successfully logged out.");
      setSnackbarOpen(true);

      // Redirect to sign-in page
      navigate("/challenge/signin");
    } catch (error) {
      console.error("Error during logout:", error);
      setSnackbarMessage("Failed to logout. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleCreateChallenge = () => {
    navigate("/challenge/create");
  };

  const handleProfileClick = () => {
    if (userProfile && userProfile.uid) {
      navigate(`/challenge/profile/${userProfile.uid}`); // Navigate to the profile edit page
    } else {
      setSnackbarMessage("Profile information not available.");
      setSnackbarOpen(true);
    }
  };

  // Toggle the Drawer open state
  const toggleDrawer = (open) => () => {
    setMenuOpen(open);
  };

  // Define the list of navigation links
  const navLinks = userProfile ? (
    <>
      <ListItem button onClick={handleLogout}>
        <ListItemText primary="Sign Out" />
      </ListItem>
      <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
      {userProfile.admin && (
        <ListItem button onClick={handleCreateChallenge}>
          <ListItemText primary="Create Challenge" />
        </ListItem>
      )}
      <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
      <ListItem button onClick={handleProfileClick}>
        <ListItemText primary="Profile" />
      </ListItem>
      <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
    </>
  ) : (
    <>
      <ListItem button component={Link} to="/challenge/signin">
        <ListItemText primary="Sign in" />
      </ListItem>
      <Divider />
      <ListItem button component={Link} to="/signup">
        <ListItemText primary="Sign up" />
      </ListItem>
    </>
  );

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: theme.colors.darkBlue,
        p: 2, // Adjusted padding for better spacing
        mb: 2,
      }}
    >
      <Grid container alignItems="center" justifyContent="space-between">
        {/* Logo and Title */}
        <Grid item sx={{ display: "flex", alignItems: "center" }}>
          {/* Image next to title */}
          <Link to="/" style={{ textDecoration: "none" }}>
            <img
              src={logo}
              alt="Kbean Logo"
              style={{
                height: "60px",
                marginRight: "15px",
                marginLeft: "15px",
              }}
            />
          </Link>
          <Link to="/challenge/" style={{ textDecoration: "none" }}>
            <Typography
              variant="h5"
              sx={{ color: theme.colors.red, fontWeight: "bold" }}
            >
              Challenge
            </Typography>
          </Link>
        </Grid>

        {/* Navigation Links for larger screens */}
        <Grid
          item
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 2,
          }}
        >
          {userProfile ? (
            <>
              <Button
                onClick={handleLogout}
                sx={{ color: "white", textTransform: "none" }}
              >
                Sign Out
              </Button>
              {userProfile.admin && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateChallenge}
                  sx={{
                    backgroundColor: theme.colors.red,
                    fontFamily: "basic-sans",
                    fontSize: "1em",
                    fontWeight: "700",
                    color: theme.colors.white,
                    borderRadius: "50px",
                    padding: "8px 20px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: theme.colors.green,
                      boxShadow: "none",
                    },
                  }}
                >
                  Create +
                </Button>
              )}
              <IconButton onClick={handleProfileClick}>
                <img
                  src={account}
                  alt="Kbean Account"
                  style={{
                    height: "35px",
                    marginLeft: "10px",
                    marginRight: "20px",
                  }}
                />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                component={Link}
                to="/challenge/signin"
                sx={{ color: "white", textTransform: "none" }}
              >
                Sign in
              </Button>
              <Button
                component={Link}
                to="/challenge/signup"
                sx={{ color: "white", textTransform: "none" }}
              >
                Sign up
              </Button>
            </>
          )}
        </Grid>

        {/* Menu Icon for smaller screens */}
        <Grid item sx={{ display: { xs: "block", sm: "none" } }}>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
          >
            <MenuIcon sx={{ color: "white" }} />
          </IconButton>
        </Grid>
      </Grid>

      {/* Drawer for smaller screens */}
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.mediumBlue, // Set Drawer background
            color: "white", // Set default text color
          },
        }}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>{navLinks}</List>
          {/* Optionally, you can add a divider and more links */}
          {/* <Divider />
          <List>
            <ListItem button component={Link} to="/another-route">
              <ListItemText primary="Another Link" />
            </ListItem>
          </List> */}
        </Box>
      </Drawer>

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Navbar;
